import { replaceDashes } from "./strings";
import { rtlRegex, type StandaloneBookData } from "@rsc/scripture-model";
import type { PartialVerse } from "../verseParts";

/**
 * Format a reference with en dashes, disambiguated compound verse labels, and RTL direction marks.
 * Append the supplied suffix unchanged; chapter labels determine whether a chapter span is shown.
 */
export function formatVerseRange(
  fromVerseLabel: string,
  toVerseLabel: string,
  bookName: string,
  fromChapterLabel: string,
  toChapterLabel: string,
  suffix: string = "",
): string {
  // We have to use "en dashes" to represent a range of numbers when
  // presenting it on the front end, not hyphens or "em dashes".
  // See https://www.thepunctuationguide.com/en-dash.html
  fromVerseLabel = replaceDashes(fromVerseLabel).trim();
  toVerseLabel = replaceDashes(toVerseLabel).trim();

  // If a verse label contains a dash, it is a compound verse.
  // Wrap it in braces, except when it's the only verse.
  if (fromVerseLabel !== toVerseLabel) {
    if (fromVerseLabel.includes("–")) {
      fromVerseLabel = `(${fromVerseLabel})`;
    }
    if (toVerseLabel.includes("–")) {
      toVerseLabel = `(${toVerseLabel})`;
    }
  }

  // Check if the book name contains RTL characters.
  const hasRtl = !!rtlRegex.exec(bookName);
  // If the book name contains RTL characters, fix the direction of the
  // chapters and verses using an LRM character.
  const directionMark = hasRtl ? "\u200e" : "";

  bookName = bookName.trim();
  bookName = bookName.length ? `${bookName} ` : "";

  if (fromChapterLabel === toChapterLabel) {
    if (fromVerseLabel === toVerseLabel) {
      return `${directionMark}${bookName}${directionMark}${fromChapterLabel}:${fromVerseLabel}${suffix}`;
    } else {
      return `${directionMark}${bookName}${directionMark}${fromChapterLabel}:${fromVerseLabel}–${toVerseLabel}${suffix}`;
    }
  } else {
    return `${directionMark}${bookName}${directionMark}${fromChapterLabel}:${fromVerseLabel}–${toChapterLabel}:${toVerseLabel}${suffix}`;
  }
}

/**
 * Append the local book name in parentheses only when it differs from the selected translation's name.
 */
export function formatBookName(
  book: StandaloneBookData,
  localBook: StandaloneBookData,
) {
  return book.name !== localBook.name
    ? `${book.name} (${localBook.name})`
    : book.name;
}

/**
 * Return a plain or HTML translation suffix, or an empty string when disabled or metadata is missing.
 */
export function getTranslationSuffix(
  enabled: boolean,
  name: string | null,
  abbr: string | null,
  plain: boolean,
): string {
  if (!enabled || !name || !abbr) {
    return "";
  }

  if (plain) {
    return ` (${abbr})`;
  } else {
    return ` (<abbr title="${name}">${abbr}</abbr>)`;
  }
}

/**
 * Find the first partial verse whose inclusive word bounds contain the ordinal, or undefined.
 */
export function findVersePartContainingOrdinal(
  parts: PartialVerse[],
  ordinal: number,
): PartialVerse | undefined {
  return parts.find((part) => {
    return part.fromOrdinal <= ordinal && part.toOrdinal >= ordinal;
  });
}

/**
 * Find the partial verse containing both endpoints of an ascending word selection.
 * Callers must supply ordered ordinals; empty or cross-part selections return undefined.
 */
export function findVersePartContainingAllOrdinals(
  parts: PartialVerse[],
  wordOrdinals: number[],
): PartialVerse | undefined {
  // Find the partial verse containing the first selected word.
  const firstWordOrdinal = wordOrdinals[0];
  const firstPart =
    firstWordOrdinal !== undefined
      ? findVersePartContainingOrdinal(parts, firstWordOrdinal)
      : undefined;

  // Find the partial verse containing the last selected word.
  const lastWordOrdinal = wordOrdinals[wordOrdinals.length - 1];
  const lastPart =
    lastWordOrdinal !== undefined
      ? findVersePartContainingOrdinal(parts, lastWordOrdinal)
      : undefined;

  return firstPart === lastPart ? firstPart : undefined;
}
